/** A read-only history request must settle even if fetch or its body stalls. */
export async function readProjectHistory<T>(path: "/api/projects" | "/api/drafts", signal: AbortSignal, timeoutMs = 15_000): Promise<T[]> {
  if (signal.aborted) throw new DOMException("Canceled", "AbortError");
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let cancel: () => void = () => {};
  try {
    const deadline = new Promise<never>((_resolve, reject) => {
      cancel = () => { reject(new DOMException("Canceled", "AbortError")); controller.abort(); };
      signal.addEventListener("abort", cancel, { once: true });
      timer = setTimeout(() => { reject(new Error("History loading timed out. Your saved videos have not been deleted. Please retry.")); controller.abort(); }, timeoutMs);
    });
    return await Promise.race([deadline, (async () => {
      const response = await fetch(path, { cache: "no-store", credentials: "same-origin", signal: controller.signal });
      if (!response.ok) throw new Error(response.status === 401
        ? "Your session expired. Sign in again to see your saved history."
        : "Could not load history. Your saved videos have not been deleted. Please retry.");
      const value: unknown = await response.json();
      if (!Array.isArray(value)) throw new Error("History returned an invalid response. Please retry.");
      return value as T[];
    })()]);
  } finally { clearTimeout(timer); signal.removeEventListener("abort", cancel); }
}
